import { useCallback, useEffect, useMemo, useState } from "react";
import { buildEmptyFormReview, deleteFormReview, getFormReviewDetail, saveFormReview } from "../services/FormReviewService";

const normalizeUpdater = (nextValue, currentValue) => (
  typeof nextValue === "function" ? nextValue(currentValue) : nextValue
);

export const useFormReview = (applicationId, options = {}) => {
  const { autoLoad = true, onLoadError } = options;
  const [formReview, setFormReview] = useState(buildEmptyFormReview(applicationId));
  const [loading, setLoading] = useState(Boolean(autoLoad && applicationId));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const loadFormReview = useCallback(async () => {
    if (!applicationId) {
      const empty = buildEmptyFormReview();
      setFormReview(empty);
      setLoading(false);
      return empty;
    }
    setLoading(true);
    setError(null);
    try {
      const nextReview = await getFormReviewDetail(applicationId);
      setFormReview(nextReview);
      return nextReview;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [applicationId]);

  const persistFormReview = useCallback(async (nextValue) => {
    setSaving(true);
    setError(null);
    try {
      const resolvedReview = normalizeUpdater(nextValue, formReview);
      const savedReview = await saveFormReview(resolvedReview);
      setFormReview(savedReview);
      return savedReview;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [formReview]);

  const removeFormReview = useCallback(async () => {
    if (!applicationId) {
      setFormReview(buildEmptyFormReview());
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await deleteFormReview(applicationId);
      setFormReview(buildEmptyFormReview(applicationId));
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [applicationId]);

  const taskCount = useMemo(() => formReview?.tasks?.length || 0, [formReview?.tasks]);

  useEffect(() => {
    if (!autoLoad) {
      return;
    }
    loadFormReview().catch((err) => {
      onLoadError?.(err);
    });
  }, [autoLoad, loadFormReview, onLoadError]);

  return {
    error,
    formReview,
    loading,
    saving,
    taskCount,
    setFormReview,
    loadFormReview,
    persistFormReview,
    removeFormReview,
  };
};

export default useFormReview;
